/**
 * Multi-bridge OPBridge store + manager.
 *
 * Owns the canonical `BridgeConfig[]` (persisted to localStorage) and
 * keeps a parallel map of live `BridgeConnection` instances, one per
 * config. CRUD operations on the config list rebuild the connection
 * map: add → start a new connection, remove → destroy, update of
 * baseUrl/bearer → rebuild that one connection.
 *
 * "List-shape from day one" — the singleton case is a list of length
 * one. The Connect page (Settings → Connect, DEC-060) reads `bridges()`
 * as the authoritative paired-list and layers scan-discovered rows on
 * top via opbridge/discovery.ts.
 */

import { createSignal, type Accessor } from 'solid-js';
import {
  type BridgeConfig,
  DEFAULT_MIDI_PORT,
  declinePeer,
  electPrimary,
  loadBridges,
  newBridgeId,
  onBridgesStorageChanged,
  saveBridges,
  saveBridgesMergingMetadata,
  undeclinePeer,
  undeclineAllPeers,
} from './storage.ts';
import {
  BridgeConnection,
  makeConnection,
  type BridgeStatus,
} from './connection.ts';
import { OpbridgeHttpClient, type OpbridgeHealth } from './httpClient.ts';
import { WtConnection, wtSupported } from './wtTransport.ts';
import { createHistory } from '../undo/history.ts';
import { panicLatched } from '../performance/panicLatch.ts';

// One-shot probe timeout for the DEC-526 suite-hostname upgrade attempt
// below — short, since it only fires once per reconcile() (not per poll)
// and a slow/unreachable suite domain must never delay real connectivity.
const SUITE_UPGRADE_PROBE_TIMEOUT_MS = 1_500;

const [bridgesSig, setBridgesSig] = createSignal<BridgeConfig[]>(loadBridges());

/** Map keyed by BridgeConfig.id. Rebuilt by reconcile(). */
const connections = new Map<string, BridgeConnection>();

// ─── The offline path: one WebTransport session per bridge ──────────────────
// Kept OUTSIDE `connections` on purpose. `reconcile()` tears down and rebuilds
// every BridgeConnection on any config commit, and `tryUpgradeToSuiteHostname`
// swaps one mid-flight; a QUIC session hanging off those would be re-dialled
// each time. The session is keyed by what it actually depends on - the dialled
// URL and the pinned hash - so it survives every rebuild that did not change
// them, and is deliberately replaced by any rebuild that did (a rotated cert).
const wtSessions = new Map<string, { key: string; conn: WtConnection }>();

/** Harness kill switch, same shape and same reasoning as
 *  `opbuttons.projectSync.disabled`.
 *
 *  WHY THIS IS NEEDED and is not a hack: the bot asserts MIDI by intercepting
 *  `POST /midi/send` at Playwright's routing layer, and routing only sees
 *  `fetch` - QUIC is invisible to it. With WebTransport preferred, ~20 smokes
 *  stopped observing anything (measured: `preset_change_fires_compound` read
 *  0 messages where it expected 2). Worse than blind assertions: several of
 *  those routes exist to MOCK a fake port, so their traffic would have gone
 *  to the real daemon and the operator's real rig instead of the mock.
 *
 *  An automated context and a real iPad look identical to the app; only the
 *  harness knows the difference, so the harness is what declares it. Real
 *  users are unaffected - absent the flag this returns false and the offline
 *  path is on. */
function wtDisabled(): boolean {
  try {
    return typeof localStorage !== 'undefined'
      && localStorage.getItem('opbuttons.webtransport.disabled') === '1';
  } catch { return false; }
}

/** Every pin known for a bridge, newest-known first, de-duplicated.
 *  Falls back to the single current pin on a daemon that publishes no chain,
 *  which is every daemon today. */
function wtPins(cfg: BridgeConfig): string[] {
  const out: string[] = [];
  const push = (h: string | null | undefined) => {
    if (!h) return;
    const clean = h.trim().toLowerCase();
    if (/^[0-9a-f]{64}$/.test(clean) && !out.includes(clean)) out.push(clean);
  };
  push(cfg.wt_cert_sha256);
  for (const h of cfg.wt_cert_hashes ?? []) push(h);
  return out;
}

/** Order-insensitive set comparison. The daemon is free to reorder its
 *  published chain between polls; treating that as a change would rebuild
 *  the QUIC session on a routine health tick for no reason. */
function sameHashes(a: string[] | null, b: string[] | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

function wtKey(cfg: BridgeConfig): string | null {
  if (!cfg.wt_url) return null;
  const pins = wtPins(cfg);
  if (pins.length === 0) return null;
  // An expired pin set can only fail the handshake, so skip the dial entirely.
  // Treated as "no offline path" rather than an error: the next successful
  // online /health re-learns a fresh one.
  //
  // Prefer the CHAIN's horizon when the daemon published one. Using the
  // current cert's own expiry there would retire a perfectly good set of
  // pre-minted future pins the moment the first one lapsed - throwing away
  // exactly the months of offline headroom the chain exists to provide.
  const exp = cfg.wt_hashes_valid_until_unix_s ?? cfg.wt_expires_at_unix_s;
  if (exp && exp * 1000 <= Date.now()) return null;
  return `${cfg.wt_url}|${pins.join(',')}`;
}

/** The WebTransport session for a row, or null when this bridge has no usable
 *  offline path (never reached while online, daemon too old to advertise one,
 *  pin expired, or a browser without WebTransport). Null is the normal case on
 *  a first boot and simply leaves `fetch` doing what it always did. */
function wtFor(cfg: BridgeConfig): WtConnection | null {
  if (!wtSupported()) return null;
  if (wtDisabled()) return null;
  const key = wtKey(cfg);
  if (!key) {
    const stale = wtSessions.get(cfg.id);
    if (stale) { stale.conn.close(); wtSessions.delete(cfg.id); }
    return null;
  }
  const existing = wtSessions.get(cfg.id);
  if (existing && existing.key === key) return existing.conn;
  if (existing) existing.conn.close();
  const conn = new WtConnection({ url: cfg.wt_url!, certHashesHex: wtPins(cfg) });
  wtSessions.set(cfg.id, { key, conn });
  return conn;
}

/** The live WebTransport session for a bridge, if one exists. Diagnostics
 *  only - read `connectionsVersion()` first inside any memo that must notice
 *  a rebuild, exactly as with `getConnection()`. */
export function getWtSession(bridgeId: string): WtConnection | null {
  return wtSessions.get(bridgeId)?.conn ?? null;
}

/**
 * A WebTransport session for a bridge that is NOT yet in the store.
 *
 * The pairing case, and the only one: at a venue with no internet a brand-new
 * device has nothing stored to derive a session from, yet the whole pairing
 * handshake (`/pairing/consumers`, `POST /pairing/request`, the status
 * long-poll) has to reach the daemon somehow. The scanned QR carries the
 * endpoint and pin, so a session can be built straight from it - before any
 * row exists.
 *
 * Routed through here rather than constructed at the call site so the support
 * check and the harness kill switch stay in ONE place. A pairing flow that
 * quietly ignored `opbuttons.webtransport.disabled` would take every pairing
 * smoke off Playwright's routing layer and blind it, which is exactly the
 * failure the switch exists to prevent.
 *
 * Not pooled: this is a short-lived session for one handshake. Once the row
 * exists, `wtFor()` owns the long-lived one. The caller closes it.
 */
export function makeAdHocWtConnection(url: string, pins: string[]): WtConnection | null {
  if (!wtSupported() || wtDisabled()) return null;
  const clean = pins.filter((h) => /^[0-9a-f]{64}$/.test(h.trim().toLowerCase()));
  if (!url || clean.length === 0) return null;
  return new WtConnection({ url, certHashesHex: clean.map((h) => h.trim().toLowerCase()) });
}

/** Bumped every time an ENTRY in `connections` is replaced or removed - a
 *  plain `Map` isn't itself reactive, so a `createMemo` that only reads
 *  `getConnection(id)` (a plain `.get()`) never re-runs when THIS bridge's
 *  connection object gets swapped for a new one, even though the memo looks
 *  like it depends on live connection state. Real bug this closes
 *  (2026-08-23): `tryUpgradeToSuiteHostname()` below replaces a connected
 *  bridge's object once it learns the suite hostname - `getConnection(id)`
 *  after that returns a DIFFERENT object than the one any earlier memo
 *  captured. `performanceMode.ts`'s `remotePerformanceModeActive` had
 *  exactly this shape and went stale forever after the swap (confirmed via
 *  `project_sync_pauses_in_performance_mode` smoke - the remote gate
 *  stopped gating within seconds of the connection upgrading). Callers that
 *  need to react to a swap should read this signal before calling
 *  `getConnection()`, purely to register the dependency. */
const [connectionsVersionSig, setConnectionsVersionSig] = createSignal(0);
function bumpConnectionsVersion(): void { setConnectionsVersionSig((v) => v + 1); }

/** Reactive "has any connection object been replaced" counter - see
 *  `connectionsVersionSig` above for why this exists. Read it (call it,
 *  ignore the value) before `getConnection()` inside any memo/effect that
 *  must notice a connection swap, not just a status/health CHANGE on the
 *  connection it already has. */
export const connectionsVersion: Accessor<number> = connectionsVersionSig;

reconcile();

// Cross-tab live sync (2026-08-13) — a bridge another tab of this same
// browser adds or updates shows up here without a manual reload. This
// module has no start/stop lifecycle (it's a page-lifetime singleton, same
// as `reconcile()` running unconditionally above), so the listener is wired
// unconditionally too; there is nothing meaningful to unsubscribe before
// the page itself goes away. See onBridgesStorageChanged's doc comment for
// the bug this pairs with (a stale tab silently erasing another tab's
// pairing on its own next routine health tick).
onBridgesStorageChanged((next) => {
  setBridgesSig(next);
  reconcile();
});

export const bridges: Accessor<BridgeConfig[]> = bridgesSig;

export function addBridge(input: {
  name: string;
  baseUrl: string;
  bearer: string | null;
  defaultMidiOutput?: string | null;
  /** v2: stable identity from OPBridge `/health.instance_name`. Empty
   *  string means "populate on next successful /health" — connection
   *  manager will fill it in. */
  bridge_instance_name?: string;
  /** Stable `node_id` from OPBridge `/health`. Normally null on add (the
   *  connection manager backfills it via reportBridgeIdentity on the first
   *  successful /health). The inheritance-drainer (DEC-465) passes it
   *  explicitly when auto-adding a peer resolved from an inheritance, so the
   *  row is dedup-idempotent by node_id BEFORE its first /health completes. */
  nodeId?: string | null;
  /** v2: bundle id granted by pair approval. Null on manual-URL add. */
  scope_bundle_id?: string | null;
  scope_bundle_version?: number | null;
  /** DEC-063: whether this row should become the primary. Defaults to
   *  true when the store is currently empty (first-pair case) — the
   *  primary invariant needs SOMETHING to point at as soon as any row
   *  exists. Explicit `false` on subsequent adds means "add as peer
   *  under existing primary." */
  is_primary?: boolean;
}): BridgeConfig {
  const currentList = bridgesSig();
  const shouldBePrimary = input.is_primary ?? (currentList.length === 0);
  const cfg: BridgeConfig = {
    id: newBridgeId(),
    name: input.name,
    baseUrl: normalizeBaseUrl(input.baseUrl),
    bearer: input.bearer,
    nodeId: input.nodeId ?? null,
    everConnected: false,
    defaultMidiOutput: input.defaultMidiOutput ?? DEFAULT_MIDI_PORT,
    bridge_instance_name: input.bridge_instance_name ?? '',
    scope_bundle_id: input.scope_bundle_id ?? null,
    scope_bundle_version: input.scope_bundle_version ?? null,
    added_at_us: Date.now() * 1000,
    is_primary: false,  // set via electPrimary below
    suite_hostname: null,
    browser_tls_port: null,
    suite_cert_expires_at_unix_s: null,
    suite_cert_renewing: false,
    // Learned on this row's first successful /health (applyHealth), which is
    // also what arms the offline path for it.
    wt_url: null,
    wt_cert_sha256: null,
    wt_expires_at_unix_s: null,
    wt_cert_hashes: null,
    wt_hashes_valid_until_unix_s: null,
  };
  let next = [...currentList, cfg];
  if (shouldBePrimary) {
    // Demote every existing primary; elect the new row.
    next = next.map((r) => ({ ...r, is_primary: r.id === cfg.id }));
  } else {
    // Not primary — enforce invariant that at least one row still is.
    next = electPrimary(next);
  }
  commit(next);
  // An explicit add always wins over a stale decline (2026-08-13) — covers
  // the user manually reconnecting a bridge they'd previously disconnected.
  // No-op when nodeId is still unknown (manual-URL add); reportBridgeIdentity
  // below covers that case once /health backfills it.
  // Clear EVERY decline, not just this node's (2026-08-28). Explicitly
  // pairing a bridge is a statement about the whole mesh - "I want these
  // machines" - and the stale veto that stranded the operator was on a
  // DIFFERENT node than the one being added, so the per-node clear below
  // could never have reached it. See storage.ts::undeclineAllPeers.
  undeclineAllPeers();
  return cfg;
}

/**
 * Put back pairings from a backup ("Import everything", plan 3.2). The rows
 * are already filtered by `bundle/restoreMerge.ts::planBridgeRestore` (no
 * duplicate of a pairing this device has) and keep their row ids (a pedal
 * still carries one beside its node id). The connection manager connects them
 * like any other row (`commit` -> `reconcile`). A restore is an explicit
 * "I want these", so a stale decline of the same OPBridge is cleared - the
 * same reasoning as `addBridge`. Returns how many were added.
 */
export function restoreBridges(rows: readonly BridgeConfig[]): number {
  if (rows.length === 0) return 0;
  const next = electPrimary([...bridgesSig(), ...rows.map((r) => ({ ...r, baseUrl: normalizeBaseUrl(r.baseUrl) }))]);
  commit(next);
  for (const r of rows) if (r.nodeId) undeclinePeer(r.nodeId);
  return rows.length;
}

/** Explicit "Disconnect" (Connect page) always wins over auto-adoption —
 *  see storage.ts's declined-peers block for the full story. Records the
 *  removed row's nodeId (if known) BEFORE filtering it out, so
 *  inheritanceDrainer.ts stops treating "no row" as "never seen." */
export function removeBridge(id: string): void {
  const removed = bridgesSig().find((b) => b.id === id);
  if (removed?.nodeId) declinePeer(removed.nodeId);
  // If removing the primary, elect a new one from the survivors so the
  // invariant "exactly one primary when list non-empty" holds.
  const next = electPrimary(bridgesSig().filter((b) => b.id !== id));
  commit(next);
}

export function updateBridge(
  id: string,
  patch: Partial<Pick<BridgeConfig,
    'name' | 'baseUrl' | 'bearer' | 'defaultMidiOutput' |
    'bridge_instance_name' | 'scope_bundle_id' | 'scope_bundle_version' | 'nodeId' | 'everConnected'
  >>,
): void {
  const next = bridgesSig().map((b) => {
    if (b.id !== id) return b;
    const merged: BridgeConfig = { ...b, ...patch };
    if (patch.baseUrl !== undefined) merged.baseUrl = normalizeBaseUrl(patch.baseUrl);
    return merged;
  });
  // Only what the operator edits about a computer is an undo step (audit
  // 2026-11-03 B-8): a fresh key, an address upgrade or a learned identity is
  // the app keeping the pairing working, and Ctrl+Z used to undo THAT first -
  // putting a dead key back - when it was the most recent step anywhere.
  commit(next, OPERATOR_FIELDS.some((k) => k in patch));
}

/** The fields of a bridge row that are the operator's to undo (B-8). Every
 *  other field - the key, the address, the learned identity, the offline door -
 *  is kept as it is NOW when an undo puts an older list back. */
const OPERATOR_FIELDS = ['name', 'defaultMidiOutput', 'is_primary'] as const;

/** Look up a bridge row by its stable `instance_name`. Returns null if
 *  no row matches (e.g. a fresh install pre-pairing). */
export function findBridgeByInstance(instanceName: string): BridgeConfig | null {
  if (!instanceName) return null;
  for (const b of bridgesSig()) {
    if (b.bridge_instance_name === instanceName) return b;
  }
  return null;
}

/** Look up a bridge row by its stable `nodeId`. The preferred identity join
 *  (DEC-465) — node_id is stable where instance_name is an mDNS label that can
 *  drift. Returns null if no row carries that node_id yet (e.g. before the
 *  first /health backfill on a manually-added row). */
export function findBridgeByNode(nodeId: string): BridgeConfig | null {
  if (!nodeId) return null;
  for (const b of bridgesSig()) {
    if (b.nodeId === nodeId) return b;
  }
  return null;
}

/** DEC-063: the primary OPBridge row. Reactive — reads bridgesSig().
 *  Returns null when the store is empty. When non-empty, the electPrimary
 *  invariant guarantees exactly one row is primary. */
export function primaryBridge(): BridgeConfig | null {
  const list = bridgesSig();
  if (list.length === 0) return null;
  return list.find((b) => b.is_primary) ?? list[0] ?? null;
}

/** DEC-063: promote a bridge to primary. Demotes any existing primary.
 *  No-op when the target is already primary. Idempotent. Persists to
 *  storage; connections do not rebuild (primary/peer status is app
 *  layer, not connection layer). */
export function setPrimary(id: string): void {
  const list = bridgesSig();
  const target = list.find((b) => b.id === id);
  if (!target) return;
  if (target.is_primary) return;
  const next = list.map((b) => ({ ...b, is_primary: b.id === id }));
  // Skip commit's reconcile — primary flip doesn't need connection
  // teardown/rebuild. Direct write + save.
  bridgeHistory.checkpoint();
  setBridgesSig(next);
  saveBridges(next);
}

/** Returns the live connection for a bridge (or null if unknown id). The
 *  badge component reads .statusSignal() / .healthSignal() off this. */
export function getConnection(id: string): BridgeConnection | null {
  const cap = captures.get(id);
  if (cap) return cap.conn;
  return connections.get(id) ?? null;
}

// ─── Capture bridges (mod sharing, 2026-09-29) ───────────────────────────
//
// "What would this item put on the wire?" answered by running the REAL send
// code against a bridge that records instead of sending. `modShare/ops.ts`
// turns the recording into the named action a mod fires, so a shared item
// and a finger press share one implementation from the dispatch layer down
// rather than two that drift apart.
//
// Surgical by construction: only a call whose target IS a capture id is
// captured. Each capture gets its own id, so concurrent captures never mix,
// and a real press during a capture resolves its real target as always.

/** One request the send code issued against a capture bridge. */
export interface CapturedRequest {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  body: unknown;
}

const CAPTURE_PREFIX = 'capture\u0000';
let captureSeq = 0;
const captures = new Map<string, { cfg: BridgeConfig; conn: BridgeConnection; log: CapturedRequest[] }>();

/** True for a capture bridge's id. Dispatch uses it to skip LOCAL side
 *  effects (the send cache, lit-state mirrors) that would otherwise record a
 *  send that never happened. */
export function isCaptureBridge(id: string | undefined): boolean {
  return !!id && id.startsWith(CAPTURE_PREFIX);
}

/**
 * Run `send(target)` against a recording stand-in for `real` and return what
 * it asked OPBridge to do. The stand-in copies `real`'s config (so ports and
 * defaults resolve exactly as they would there), always reads as connected,
 * and answers every request with a generic success. Nothing leaves the
 * browser.
 *
 * "Success" has to be the success the send code checks for. A route whose
 * 200 can still mean "nothing heard it" says so in its body, and the send
 * code stops at the first such answer - so a bare `{ok:true}` there would
 * record only the FIRST message of a multi-message press. `/opvisual/trigger`
 * is the one today (DEC-215: `opvisual_connected`), answered as a linked
 * OPVisual would answer it.
 */
export async function captureRequests(
  real: BridgeConfig,
  send: (target: string) => Promise<unknown>,
): Promise<CapturedRequest[]> {
  const id = `${CAPTURE_PREFIX}${++captureSeq}`;
  const log: CapturedRequest[] = [];
  const client = new OpbridgeHttpClient('capture://', null);
  (client as unknown as { request: (m: CapturedRequest['method'], path: string, body?: unknown) => Promise<unknown> }).request =
    async (method, path, body) => {
      log.push({ method, path, body: body === undefined ? undefined : JSON.parse(JSON.stringify(body)) });
      if (path === '/opvisual/trigger') return { ok: true, opvisual_connected: true };
      return { ok: true };
    };
  const realConn = connections.get(real.id);
  const conn = {
    httpClient: () => client,
    status: () => 'connected' as const,
    health: () => realConn?.health() ?? null,
  } as unknown as BridgeConnection;
  captures.set(id, { cfg: { ...real, id }, conn, log });
  try {
    await send(id);
  } finally {
    captures.delete(id);
  }
  return log;
}

/** Per-item bridge affinity resolution — supersedes DEC-063's primary-only
 *  dispatch. `target` is an item's/pedal's `bridgeId`: empty or undefined
 *  resolves to the default bridge (today's `is_primary` row, same fallback
 *  `primaryBridge()` uses); a specific id resolves to that row, or `null`
 *  if it doesn't match any known bridge (removed/renamed) — never silently
 *  substitutes a different bridge (DEC-138: must say so, never dead-tap).
 *  Dispatch (`opbridge/dispatch.ts`) and per-item liveness
 *  (`bridgeConnectedReactive` below) both resolve through this so they can
 *  never disagree about the same item. */
/**
 * The key a per-bridge readback mirror (OBS scenes, Ableton device state,
 * Run Program lists) stores and reads a bridge's state under: the LOCAL ROW
 * id, which is what `getConnection` and dispatch's `cfg.id` both use.
 *
 * ⚠️ Every poller and face that keys state by a bridge MUST go through this,
 * never use an item's `bridgeIds` entry raw. The "Sends to" picker stores the
 * mesh NODE id (so the aim survives project sync), and a node id handed to
 * `getConnection` finds nothing - the poll records "No such bridge" under the
 * node id and the face reads that. Measured 2026-09-28: an Ableton Device
 * button aimed through the picker read "device error" against a bridge
 * answering `/devices/status` 200, while pressing it still worked (dispatch
 * already resolved). `_probe_node_aimed_faces.ts` reproduces it.
 *
 * An id that resolves to nothing is returned UNCHANGED, so a machine removed
 * from the rig still reads "No such bridge" rather than going quiet. Empty
 * stays empty: it means "not aimed", and must not become the default bridge.
 */
export function bridgeStateKey(id: string | undefined): string {
  if (!id) return '';
  return resolveBridge(id)?.id ?? id;
}

export function resolveBridge(target: string | undefined): BridgeConfig | null {
  if (!target) return primaryBridge();
  const cap = captures.get(target);
  if (cap) return cap.cfg;
  const list = bridgesSig();
  // LOCAL ROW ID FIRST, then the stable node id.
  //
  // ⚠️ This is the single chokepoint every outbound dispatch resolves
  // through, and the second lookup is what makes per-item bridge affinity
  // survive the mesh. `newBridgeId()` mints a DEVICE-LOCAL id
  // (`Math.random()` plus a timestamp), and items live on pages, pages on
  // `Project`, and `layout/projectSync.ts` pushes the whole `Project` as the
  // document body - so a row id stored in `item.bridgeIds` replicated
  // mesh-wide and resolved to nothing on every other machine. Node ids are
  // the join key DEC-465 designates for exactly this.
  //
  // Order matters and row-id-first is deliberate: a machine that still holds
  // a legacy row id keeps working unchanged, so the migration can be lazy
  // rather than a flag day. The two id spaces do not collide in practice
  // either - row ids carry a `br_` prefix and node ids are bare hex
  // (`6884114c6d79ae8d`) - but the ordering makes that a convenience rather
  // than something correctness rests on.
  //
  // An unresolvable target returns null, which every caller already renders
  // as "not connected". That is the honest state while a freshly-paired
  // row waits for `reportBridgeIdentity` to backfill its node, and it is
  // why NO sentinel is needed here (unlike the pedal path, where the empty
  // string already meant "the default bridge").
  return list.find((b) => b.id === target)
    ?? list.find((b) => b.nodeId === target)
    ?? null;
}

/** Fan-out variant of resolveBridge. `'all'` resolves to every bonded
 *  bridge (offline rows are NOT filtered out here — each per-bridge
 *  dispatch call already checks its own connection status and reports
 *  tried-and-failed, so the caller can still surface which target
 *  actually failed rather than silently omitting it). Anything else
 *  delegates to resolveBridge, wrapped in an array (empty when
 *  unresolved).
 *
 *  THE PANIC-LATCH GATE LIVES HERE (2026-09-03), because this is the one
 *  function EVERY outbound path resolves through — `dispatch.ts`'s ~18 entry
 *  points and `timeline/bridgeSync.ts`'s senders alike. While the latch is
 *  engaged this returns zero targets, so every caller's existing
 *  `targets.length === 0` branch fires and reports not-sent through a path
 *  that is already tested, rather than each one growing its own check.
 *
 *  It DEFAULTS TO BLOCKED on purpose. A dispatch path added next year is
 *  suppressed without anyone remembering this exists; opting out is explicit
 *  and currently has exactly two legitimate reasons:
 *    - the panic path itself, which must still reach every bridge while latched
 *      (that is the entire point of the latch), and
 *    - pure membership/liveness READS like `targetsIncludeBridge`, which ask
 *      "would this target resolve to that bridge" and send nothing.
 *  See `performance/panicLatch.ts` for the semantics and the known boundary. */
export function resolveBridges(
  target: string | undefined,
  opts?: { ignorePanicLatch?: boolean },
): BridgeConfig[] {
  // A capture is not a send: the panic latch must not blank a share.
  if (target && captures.has(target)) return [captures.get(target)!.cfg];
  if (panicLatched() && !opts?.ignorePanicLatch) return [];
  if (target === 'all') return bridgesSig();
  const cfg = resolveBridge(target);
  return cfg ? [cfg] : [];
}

/** Per-item connected check, resolved the SAME way dispatch resolves a
 *  target (via resolveBridge) — an item's "am I live" indicator and its
 *  actual send target can never disagree. Reactive. */
export function bridgeConnectedReactive(target: string | undefined): boolean {
  // Read the version FIRST: `connections` is a plain Map, and a rebuilt row
  // (rename, MIDI out change, bearer refresh, undo, a WT re-mint) replaces
  // the connection object. Without this a reader stayed subscribed to the
  // destroyed one, frozen 'connected' (dead taps) or 'unreachable'
  // (disabled while healthy) - 2026-11-03 audit B-1. Fixed here, at the
  // predicate, so every caller gets it.
  connectionsVersion();
  const cfg = resolveBridge(target);
  if (!cfg) return false;
  const conn = connections.get(cfg.id);
  return conn?.statusSignal()() === 'connected';
}

/** The primary bridge's HTTP client, only when it's actually connected —
 *  `null` otherwise. Shared so callers don't each grow their own copy of
 *  "find primary, get its connection, check status, unwrap the client"
 *  (this used to be a private helper duplicated in `helix/library.ts`). */
export function primaryHttpClient() {
  const primary = primaryBridge();
  const conn = primary ? getConnection(primary.id) : null;
  if (!primary || !conn || conn.status() !== 'connected') return null;
  return conn.httpClient();
}

/** Multi-target variant of `bridgeConnectedReactive`, for an item's
 *  `bridgeIds` (multi-select bridge affinity, 2026-08-13). `undefined`
 *  resolves to the single default bridge, same as the single-target
 *  version. True when AT LEAST ONE resolved target is connected — a
 *  partial fan-out (2 of 3 configured bridges up) still usefully fires,
 *  so the item stays enabled rather than going fully dead over one
 *  offline machine. An explicit empty list is never live (nothing
 *  configured to send to). Reactive. */
export function anyBridgeConnectedReactive(bridgeIds: string[] | undefined): boolean {
  const ids = bridgeIds === undefined ? [undefined] : bridgeIds;
  return ids.some((id) => bridgeConnectedReactive(id));
}

/** HTTP client for a specific bridge-affinity target (a pedal's/item's
 *  `bridgeId`), only when that bridge is actually connected — `null`
 *  otherwise. Same shape as `primaryHttpClient()` but resolves through
 *  `resolveBridge()`, so a caller that needs to act on a SPECIFIC bridge
 *  (e.g. probing MIDI ports for a pedal wired to a non-default machine)
 *  doesn't each grow their own "resolve target, get connection, check
 *  status, unwrap the client" boilerplate. Passing `undefined` behaves
 *  exactly like `primaryHttpClient()` (resolves to the default bridge). */
export function httpClientFor(target: string | undefined) {
  const cfg = resolveBridge(target);
  const conn = cfg ? getConnection(cfg.id) : null;
  if (!cfg || !conn || conn.status() !== 'connected') return null;
  return conn.httpClient();
}

/** Convenience: "is at least one bridge connected right now?" — drives
 *  the small header badge. */
export function anyConnected(): boolean {
  for (const conn of connections.values()) {
    if (conn.status() === 'connected') return true;
  }
  return false;
}

/** Reactive variant of anyConnected — DEC-063: the answer is
 *  "primary is connected." Peers don't count for dispatcher purposes. */
export function anyConnectedReactive(): boolean {
  connectionsVersion(); // see bridgeConnectedReactive
  const primary = primaryBridge();
  if (!primary) return false;
  const conn = connections.get(primary.id);
  if (!conn) return false;
  return conn.statusSignal()() === 'connected';
}

/** Aggregated status for the header badge light (operator 2026-11-03, decision 4: "amber when any is down"). Every
 *  paired computer counts: all up = connected (green), some up = partial (amber, no flash), none = unreachable (red,
 *  flashing). It followed the default alone (DEC-063, superseded by DEC-169's per-item targets), so the Mac (Helix)
 *  dropping left it green and the default dropping flashed critical while the others still played. Each row keeps the
 *  old grace: a connected bridge stays up through two /health misses (`connection.ts`, `UNREACHABLE_AFTER_MISSES`),
 *  and one that connected before is not called down until `CONFIRMED_OFFLINE_THRESHOLD` failures. */
const CONFIRMED_OFFLINE_THRESHOLD = 2;
export function aggregateStatus(): 'none' | 'connected' | 'partial' | 'unreachable' | 'unpaired' {
  connectionsVersion(); // see bridgeConnectedReactive
  const cfgs = bridgesSig();
  if (cfgs.length === 0) return 'none';
  let up = 0;
  let unpaired = 0;
  for (const cfg of cfgs) {
    const conn = connections.get(cfg.id);
    if (!conn) continue;
    const s = conn.statusSignal()();
    const failures = conn.consecutiveFailuresSignal()();
    if (s === 'unpaired') { unpaired++; continue; }
    const live = s === 'connected'
      || (s === 'connecting' && cfg.everConnected)
      || (s === 'unreachable' && cfg.everConnected && failures < CONFIRMED_OFFLINE_THRESHOLD);
    if (live) up++;
  }
  if (unpaired === cfgs.length) return 'unpaired';
  if (up === cfgs.length) return 'connected';
  return up > 0 ? 'partial' : 'unreachable';
}

function commit(next: BridgeConfig[], undoable = true): void {
  if (undoable) bridgeHistory.checkpoint();
  setBridgesSig(next);
  saveBridges(next);
  reconcile();
}

/** History handle for the bridge-config list. Covers add/remove/rename,
 *  baseUrl/bearer/defaultMidiOutput edits — everything through `commit`.
 *  Undo restoring an old list triggers `reconcile()` which tears down +
 *  rebuilds connections symmetrically with forward operations.
 *
 *  Connect/disconnect actions themselves are NOT part of history — they
 *  live in the `connections` Map, derived from `bridgesSig()`. Undo
 *  restores config; the connection loop reconciles from there. */
export const bridgeHistory = createHistory<BridgeConfig[]>({
  name: 'bridges',
  read: () => bridgesSig(),
  write: (snapshot) => {
    // An undo puts back the operator's edits, never an older key (B-8). A row
    // still here keeps its current credentials and identity and takes only the
    // snapshot's name, MIDI output and default; a row the undo brings back (a
    // removal undone) comes back as it was.
    const now = new Map(bridgesSig().map((b) => [b.id, b]));
    const next = snapshot.map((b) => {
      const cur = now.get(b.id);
      return cur ? { ...cur, name: b.name, defaultMidiOutput: b.defaultMidiOutput, is_primary: b.is_primary } : b;
    });
    setBridgesSig(next);
    saveBridges(next);
    reconcile();
  },
});

/** Idempotent one-shot identity backfill. Called by the connection
 *  manager once /health returns success. Persists `bridge_instance_name`
 *  + `nodeId` + `everConnected` to storage when they differ. NEVER
 *  triggers a reconcile — this is a metadata update, not a config edit.
 *  Reads the freshest `bridgesSig()` snapshot so concurrent updates
 *  don't clobber each other.
 *
 *  Also caches `suite_hostname` / `browser_tls_port` (DEC-526) when the
 *  responding daemon advertises them — pure metadata, never written to
 *  `baseUrl`. See `BridgeConfig.suite_hostname` for why the two stay
 *  separate. */
export function reportBridgeIdentity(
  bridgeId: string,
  instanceName: string | null,
  nodeId: string | null,
  suiteHostname?: string | null,
  browserTlsPort?: number | null,
  /** `/health.webtransport` + `/health.ip`, folded into the row so the
   *  offline path is on disk BEFORE it is needed. See `BridgeConfig.wt_url`
   *  for why caching is the mechanism rather than an optimisation. */
  wt?: {
    url: string;
    certSha256: string;
    expiresAtUnixS: number;
    certHashes: string[] | null;
    hashesValidUntilUnixS: number | null;
  } | null,
  /** `/health.suite_cert_expires_at_unix_s`, raw. Passed alongside the RAW
   *  `suiteHostname` above (not the cached one) because the pair of them is
   *  the signal: expiry-without-hostname means the daemon has put this cert
   *  inside its renewal window. See `BridgeConfig.suite_cert_renewing`. */
  suiteCertExpiresAtUnixS?: number | null,
): void {
  const list = bridgesSig();
  let changed = false;
  let wtAdvertMoved = false;
  const patched = list.map((cfg) => {
    if (cfg.id !== bridgeId) return cfg;
    const nextInstance = instanceName || cfg.bridge_instance_name;
    const nextNodeId = nodeId ?? cfg.nodeId;
    // AUTHORITATIVE, not replace-never-clear - and deliberately unlike the WT
    // fields below, which this line used to share an idiom with.
    //
    // OPBridge stops advertising `suite_hostname` the moment the suite cert
    // enters its 30-day renewal window, while still reporting the expiry
    // (`suite_cert.rs::health_fields`). That withholding IS a signal - their
    // own comment calls it "a cert inside the renewal window should not be
    // advertised as a name to connect to" - and a `??` here swallowed it,
    // keeping the cached name in use right up to the moment the cert expired
    // and the browser started rejecting it mid-set.
    //
    // Clearing is cheap and safe: `baseUrl` is untouched and always works, so
    // the worst case is dropping back to the LAN address about a month earlier
    // than strictly forced. The WT fields keep `??` because their cache is what
    // makes an OFFLINE venue work at all; the suite hostname needs DNS, so
    // holding a stale one buys nothing offline and costs a stranding online.
    //
    // Only reached on a SUCCESSFUL poll (sole caller is applyHealth), so a
    // null here always means the daemon said null - never that it was unreachable.
    const nextSuiteHostname = suiteHostname ?? null;
    const nextBrowserTlsPort = browserTlsPort ?? cfg.browser_tls_port;
    // A daemon that stops advertising (listener failed to bind on its last
    // restart) must not silently keep the old pin alive — but neither should
    // a single absent field wipe a cached path we may need offline. So an
    // advert only ever REPLACES, never clears: `wt === undefined/null` leaves
    // what is stored, and expiry (checked in `wtKey`) retires it instead.
    const nextWtUrl = wt?.url ?? cfg.wt_url;
    const nextWtHash = wt?.certSha256 ?? cfg.wt_cert_sha256;
    const nextWtExp = wt?.expiresAtUnixS ?? cfg.wt_expires_at_unix_s;
    const nextWtHashes = wt?.certHashes ?? cfg.wt_cert_hashes;
    const nextWtHorizon = wt?.hashesValidUntilUnixS ?? cfg.wt_hashes_valid_until_unix_s;
    // Expiry follows the replace-never-clear rule above. `renewing` deliberately
    // does NOT: it is a statement about THIS poll, and a stale one would keep
    // claiming a renewal is in flight long after the cert was replaced.
    const nextSuiteCertExp = suiteCertExpiresAtUnixS ?? cfg.suite_cert_expires_at_unix_s;
    const nextSuiteCertRenewing =
      suiteCertExpiresAtUnixS != null && (suiteHostname ?? null) === null;
    if (
      cfg.bridge_instance_name === nextInstance &&
      cfg.nodeId === nextNodeId &&
      cfg.everConnected &&
      cfg.suite_hostname === nextSuiteHostname &&
      cfg.browser_tls_port === nextBrowserTlsPort &&
      cfg.wt_url === nextWtUrl &&
      cfg.wt_cert_sha256 === nextWtHash &&
      cfg.wt_expires_at_unix_s === nextWtExp &&
      sameHashes(cfg.wt_cert_hashes, nextWtHashes) &&
      cfg.wt_hashes_valid_until_unix_s === nextWtHorizon &&
      cfg.suite_cert_expires_at_unix_s === nextSuiteCertExp &&
      cfg.suite_cert_renewing === nextSuiteCertRenewing
    ) return cfg;
    changed = true;
    if (
      cfg.wt_url !== nextWtUrl ||
      cfg.wt_cert_sha256 !== nextWtHash ||
      !sameHashes(cfg.wt_cert_hashes, nextWtHashes)
    ) wtAdvertMoved = true;
    return {
      ...cfg,
      bridge_instance_name: nextInstance,
      nodeId: nextNodeId,
      everConnected: true,
      suite_hostname: nextSuiteHostname,
      browser_tls_port: nextBrowserTlsPort,
      wt_url: nextWtUrl,
      wt_cert_sha256: nextWtHash,
      wt_expires_at_unix_s: nextWtExp,
      wt_cert_hashes: nextWtHashes,
      wt_hashes_valid_until_unix_s: nextWtHorizon,
      suite_cert_expires_at_unix_s: nextSuiteCertExp,
      suite_cert_renewing: nextSuiteCertRenewing,
    };
  });
  if (!changed) return;
  setBridgesSig(patched);
  // Merge-on-save (2026-08-13), not a raw overwrite — this is a metadata
  // backfill, not a config edit (see saveBridgesMergingMetadata's own doc
  // comment for the live multi-tab bug this closes). A routine health-tick
  // in THIS tab must never erase a bridge a different tab of the same
  // browser added in the meantime.
  saveBridgesMergingMetadata(patched);
  // A manual-URL add starts with nodeId null and only learns it here, on
  // first /health — covers reconnecting a previously-declined bridge by
  // typing its URL back in rather than via addBridge()'s own nodeId path.
  if (nodeId) undeclinePeer(nodeId);
  // The offline path was just LEARNED (first ever online poll) or ROTATED
  // (the daemon minted a new <=14-day cert). Either way the live connection
  // is holding a client with the wrong session — or none — so rebuild this
  // ONE bridge. Deliberately not a full `reconcile()`: that rebuilds every
  // connection on the mesh, and this fires on a routine health tick.
  //
  // The `changed` guard above is what keeps this from churning: once the
  // advert is stored, subsequent ticks report the identical values and
  // return before reaching here.
  if (wtAdvertMoved) rebuildConnection(bridgeId);
}

/**
 * Arm the offline path from a SCANNED advert rather than from `/health`.
 *
 * This is the break-glass path, and the case it exists for is specific: a
 * device that has been away longer than its cached pin's life arrives at a
 * venue with no internet. Its pin is dead, and the only channel that could
 * teach it a new one - `/health` behind a name that will not resolve - is
 * exactly the thing that is down. A QR carries the pin over light instead,
 * off the dashboard already running on the laptop beside it.
 *
 * Applies to every row that matches the advert's HOST, because that is what
 * identifies the machine here: the row's `baseUrl` is usually a suite
 * hostname while the advert is a LAN IP, so matching on URL would never fire.
 * A row the link reaches only through its `bridge=` URL, with the advert on
 * a host the row has never used, is NOT armed here: that is a door move,
 * listed by `scannedAdvertNewDoors` for the operator to confirm (2026-10-06).
 * There is no primary-row fallback (2026-09-02, F5).
 *
 * Returns the number of rows armed, so the caller can tell the operator
 * whether anything actually happened rather than showing a silent success.
 */
export function applyScannedWtAdvert(
  advert: {
    url: string;
    pins: string[];
    untilUnixS: number | null;
  },
  /** Arm exactly this row instead of host-matching.
   *
   *  Required by the pairing path, and not a convenience: a row created by a
   *  fresh pair stores the SUITE HOSTNAME as its `baseUrl` while the advert
   *  is a LAN IP, so host-matching cannot find it - and the primary-row
   *  fallback below would then arm a DIFFERENT, already-paired bridge with
   *  this bridge's pin. On a second pairing that is silent corruption of a
   *  working row, so the caller names its target explicitly. */
  opts: {
    bridgeId?: string;
    /** The `bridge=` URL carried by the SAME scanned link as this advert.
     *
     *  Added 2026-09-02 (security audit F5). A pairing link is self-naming: it
     *  carries the bridge's own URL alongside the advert, so there is never a
     *  need to guess which row an advert belongs to. Matching on this closes
     *  the gap the advert host alone cannot - a freshly-paired row stores the
     *  SUITE HOSTNAME as its `baseUrl` while the advert is a LAN IP, which is
     *  precisely why the old primary-row fallback existed. */
    linkBridgeUrl?: string;
  } = {},
): number {
  if (!advert.url || advert.pins.length === 0) return 0;
  try { new URL(advert.url); } catch { return 0; }

  const list = bridgesSig();
  if (opts.bridgeId) {
    const target = list.find((c) => c.id === opts.bridgeId);
    if (!target) return 0;
    return armRows(list, new Set([target.id]), advert);
  }

  // ─── NO PRIMARY-ROW FALLBACK (2026-09-02, security audit F5) ───────────
  //
  // This used to fall back to `list.filter(c => c.is_primary)` when nothing
  // matched. This function's own doc comment already warned that arming a
  // row the advert does not belong to is "silent corruption of a working
  // row" - but only the pairing caller acted on it by passing `bridgeId`;
  // the QR scanner caller did not, so a scanned code whose `wt=` host
  // matched nothing re-pointed the operator's PRIMARY bridge's WebTransport
  // URL and cert pins at whatever the code said. Since CP-117 / DEC-195
  // WebTransport is the PREFERRED transport, so that is the gig path.
  //
  // MEASURED before and after: `_probe_scanned_advert_hijacks_primary.ts` (now the smoke `scanned_advert_never_arms_the_primary`).
  // Pre-fix run 1 armed the primary of a two-row mesh from an advert
  // matching neither row.
  //
  // Why removing it does NOT break the break-glass rescue, which is the
  // thing that must not break: `linkBridgeUrl` above now covers the case the
  // fallback was written for. A device whose pin expired still has its row,
  // and the scanned link names that row by its stored `baseUrl`. A device
  // with NO row yet was never helped by this fallback anyway - with an empty
  // list there is no primary to fall back to, it already returned 0, and the
  // pairing path arms the new row explicitly with a `bridgeId` afterwards.
  //
  // ─── A NEW DOOR IS NEVER SILENT (2026-10-06, OPMod's ask) ──────────────
  //
  // `linkBridgeUrl` comes from the same untrusted link as the advert, so a
  // link naming a real paired bridge in `bridge=` and any host in `wt=` used
  // to re-point that row's door, and its bearer rides every WebTransport
  // request. Only rows whose EXISTING door (or the address they were paired
  // at) is the advert's host are armed here: that is the pin refresh the
  // break-glass rescue needs. A row the link reaches only through `bridge=`
  // is a door MOVE, returned by `scannedAdvertNewDoors` for the operator to
  // confirm and then armed by id. A move is legitimate (a venue's DHCP gives
  // the bridge a new address), so it is asked, not refused.
  const { refresh } = classifyAdvertRows(list, advert.url, opts.linkBridgeUrl);
  if (refresh.size === 0) return 0;
  return armRows(list, refresh, advert);
}

/** Rows a scanned link would give a door on a DIFFERENT host than any they
 *  already trust (see `applyScannedWtAdvert`). Each needs the operator's yes
 *  before `applyScannedWtAdvert(advert, { bridgeId })` arms it. */
export function scannedAdvertNewDoors(
  advert: { url: string },
  linkBridgeUrl: string | undefined,
): BridgeConfig[] {
  const list = bridgesSig();
  const { move } = classifyAdvertRows(list, advert.url, linkBridgeUrl);
  return list.filter((c) => move.has(c.id));
}

function classifyAdvertRows(
  list: BridgeConfig[],
  advertUrl: string,
  linkBridgeUrl: string | undefined,
): { refresh: Set<string>; move: Set<string> } {
  const hostOf = (raw: string | null | undefined): string | null => {
    if (!raw) return null;
    try { return new URL(raw).hostname; } catch { return null; }
  };
  const host = hostOf(advertUrl);
  const linkHost = hostOf(linkBridgeUrl);
  const refresh = new Set<string>();
  const move = new Set<string>();
  if (!host) return { refresh, move };
  for (const cfg of list) {
    const known = [cfg.wt_url, cfg.baseUrl].map(hostOf).filter((h): h is string => !!h);
    if (known.includes(host)) refresh.add(cfg.id);
    else if (linkHost && known.includes(linkHost)) move.add(cfg.id);
  }
  return { refresh, move };
}

/** Write a scanned advert onto a chosen set of rows and rebuild them. Shared
 *  by the host-matched and explicit-id paths so the two cannot drift in what
 *  they persist. */
function armRows(
  list: BridgeConfig[],
  targets: Set<string>,
  advert: { url: string; pins: string[]; untilUnixS: number | null },
): number {
  const [current, ...rest] = advert.pins;
  const patched = list.map((cfg) => targets.has(cfg.id)
    ? {
        ...cfg,
        wt_url: advert.url,
        wt_cert_sha256: current!,
        wt_cert_hashes: rest.length > 0 ? advert.pins : null,
        // A scanned advert carries no per-cert expiry, so trust the link's
        // horizon when it gave one and otherwise leave the field null. Null
        // means `wtKey` falls back to the current cert's own expiry - which
        // is also null here, so the pin is simply used until it fails. That
        // is the right call for a break-glass path: refusing to dial on an
        // unknown expiry would defeat the entire point of the rescue.
        wt_expires_at_unix_s: advert.untilUnixS,
        wt_hashes_valid_until_unix_s: advert.untilUnixS,
      }
    : cfg);

  setBridgesSig(patched);
  saveBridgesMergingMetadata(patched);
  for (const id of targets) rebuildConnection(id);
  return targets.size;
}

/** Rebuild exactly one bridge's connection, preserving everything else.
 *  Used when a row's transport inputs changed under a live connection. */
function rebuildConnection(bridgeId: string): void {
  const cfg = bridgesSig().find((b) => b.id === bridgeId);
  if (!cfg) return;
  const existing = connections.get(bridgeId);
  if (!existing) return;
  existing.destroy();
  const conn = makeConnection(cfg.baseUrl, cfg.bearer, (h) => applyHealth(bridgeId, h), wtFor(cfg));
  connections.set(bridgeId, conn);
  bumpConnectionsVersion();
  conn.start();
}

/** The single health observer, shared by every place that builds a
 *  connection. Was duplicated three ways, which is how a field added to one
 *  call site quietly went unlearned on the upgraded connection. */
function applyHealth(bridgeId: string, h: OpbridgeHealth): void {
  const nodeId = (h as unknown as { node_id?: string }).node_id ?? null;
  const advert = h.webtransport;
  const host = h.ip ?? null;
  // Both halves are required: the port comes from the advert, the address
  // from `/health.ip` (the daemon's own multi-homed-aware view). Without a
  // host there is nothing to dial, so no advert is recorded rather than one
  // guessed from `baseUrl` - a bridge reached via loopback or a suite
  // hostname would otherwise cache an address no other device can use.
  const wt = advert?.port && advert.cert_sha256 && host
    ? {
        url: `https://${host}:${advert.port}`,
        certSha256: advert.cert_sha256,
        expiresAtUnixS: advert.expires_at_unix_s ?? 0,
        // Absent on every daemon today (the chain is an open OPBridge ask).
        // `null` leaves whatever is stored untouched rather than clearing a
        // chain a newer daemon had previously handed us.
        certHashes: Array.isArray(advert.cert_sha256_upcoming)
          ? advert.cert_sha256_upcoming
          : null,
        hashesValidUntilUnixS: advert.upcoming_valid_until_unix_s ?? null,
      }
    : null;
  reportBridgeIdentity(
    bridgeId,
    h.instance_name ?? null,
    nodeId,
    h.suite_hostname ?? null,
    h.browser_tls_port ?? null,
    wt,
    h.suite_cert_expires_at_unix_s ?? null,
  );
}

function reconcile(): void {
  const list = bridgesSig();
  const wantIds = new Set(list.map((b) => b.id));

  // Tear down removed bridges.
  for (const [id, conn] of connections) {
    if (!wantIds.has(id)) {
      conn.destroy();
      connections.delete(id);
      bumpConnectionsVersion();
    }
  }
  // ...and their WebTransport sessions, which live in their own map and are
  // therefore NOT closed by the loop above. A Disconnect would otherwise
  // leave an open QUIC session to a bridge the user just removed - still
  // dialling, still holding the socket, for the life of the page.
  for (const [id, entry] of wtSessions) {
    if (!wantIds.has(id)) {
      entry.conn.close();
      wtSessions.delete(id);
    }
  }

  // Add or rebuild as needed. We always rebuild on commit to keep things
  // simple — the alternative is fingerprinting (baseUrl, bearer) and
  // skipping rebuild when neither changed. Polling overhead is trivial
  // (one /health every few seconds); simpler beats micro-optimal here.
  for (const cfg of list) {
    const existing = connections.get(cfg.id);
    if (existing) {
      existing.destroy();
    }
    // Capture id in closure — observer fires whenever /health returns
    // success, and persists identity fields to storage. Idempotent —
    // no-op once the row already carries the correct values.
    const id = cfg.id;
    // `wtFor` is null until this bridge has been reached once while online,
    // so a fresh install behaves exactly as it did before this shipped.
    const conn = makeConnection(cfg.baseUrl, cfg.bearer, (h) => applyHealth(id, h), wtFor(cfg));
    connections.set(cfg.id, conn);
    bumpConnectionsVersion();
    conn.start();
    // Opportunistic, non-blocking upgrade attempt (DEC-526) — never
    // delays or replaces the connection above unless it actually
    // succeeds. See tryUpgradeToSuiteHostname() for the fallback story.
    void tryUpgradeToSuiteHostname(id, cfg, conn);
  }
}

/** One-shot, best-effort attempt to move a freshly-created connection
 *  from its stored `baseUrl` onto the cached `suite_hostname` (DEC-526) —
 *  the real, publicly-trusted per-bridge hostname, when one is known from
 *  a prior successful connection. Fire-and-forget: on ANY failure
 *  (offline gig, DNS unreachable, cert not yet propagated, or `baseUrl`
 *  already IS the suite URL) this is a silent no-op and the connection
 *  keeps polling `baseUrl` exactly as it always has — `baseUrl` itself is
 *  never read from or written to here. Runs once per reconcile() call,
 *  not once per poll, so a consistently-unreachable suite domain costs
 *  one bounded probe per boot/reconnect, not ongoing overhead. */
async function tryUpgradeToSuiteHostname(
  id: string,
  cfg: BridgeConfig,
  current: BridgeConnection,
): Promise<void> {
  if (!cfg.suite_hostname || !cfg.browser_tls_port) return;
  const suiteUrl = normalizeBaseUrl(`https://${cfg.suite_hostname}:${cfg.browser_tls_port}`);
  if (suiteUrl === normalizeBaseUrl(cfg.baseUrl)) return; // already on it
  const probe = new OpbridgeHttpClient(suiteUrl, null, SUITE_UPGRADE_PROBE_TIMEOUT_MS);
  let ok = false;
  try {
    const h = await probe.health();
    ok = h.ok && h.app === 'OPBridge';
  } catch {
    ok = false;
  }
  if (!ok) return;
  // Guard against races: only swap if this is still the live connection
  // for this id — a later reconcile() (config edit, removeBridge()) may
  // have already torn it down or replaced it while the probe was in
  // flight. If so, the newer connection wins; drop this upgrade.
  if (connections.get(id) !== current) return;
  current.destroy();
  // Carry the WebTransport session across the upgrade. The two are
  // independent by construction - WT dials the LAN IP and the suite hostname
  // is a fetch URL - so this swaps only the FALLBACK, and dropping the
  // session here would silently disarm the offline path on every bridge that
  // has a suite hostname, which is all of them.
  const upgraded = makeConnection(suiteUrl, cfg.bearer, (h) => applyHealth(id, h), wtFor(cfg));
  connections.set(id, upgraded);
  bumpConnectionsVersion();
  upgraded.start();
}

function normalizeBaseUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

/** True iff at least one stored bridge carries a bearer — i.e. the app
 *  booted with a live pairing. This is the connectivity-INDEPENDENT signal
 *  the "no re-pair on update" contract rests on: a same-origin PWA update
 *  reloads localStorage, `loadBridges()` rehydrates the paired row, and this
 *  must stay true. A schema-key bump without a forward-migrator would drop
 *  the row → this flips false → the `pairing_bearer_survives_update` smoke
 *  goes red. Reactive (reads `bridgesSig()`). */
export function hasPairedBridge(): boolean {
  return bridgesSig().some((b) => !!b.bearer);
}

/** Re-export so callers can write `import { BridgeStatus } from '../opbridge/store.ts'`
 *  without reaching into connection.ts directly. */
export type { BridgeConfig } from './storage.ts';
export type { BridgeStatus } from './connection.ts';

// ─── DEV test hook (bridge / pairing state) ─────────────────────────────────
// Mirrors __opbuttonsClock (clockPoll) + __opbuttonsConsumer (consumerWs): a
// DEV-only window handle so bot smokes can assert the app's IN-MEMORY pairing
// state after a boot — not just what's serialized in localStorage. The
// pairing-persistence guard-rail needs `paired()` to prove the stored bearer
// was actually loaded, since a future storage-key bump could leave the blob in
// place (localStorage assertion passes) while the app ignores it (this fails).
// Stripped from production builds by the `import.meta.env.DEV` gate.
declare global {
  interface Window {
    __opbuttonsBridges?: {
      list(): BridgeConfig[];
      status(): 'none' | 'connected' | 'partial' | 'unreachable' | 'unpaired';
      paired(): boolean;
      primaryId(): string | null;
      /** The capabilities the app last read from this bridge's /health (row id or node id; null before a read). A smoke
       *  that changes a capability waits on this, not on a /health request it saw (2026-10-16: several things poll
       *  /health, so seeing one did not mean the connection had read it). */
      capabilities(id: string): Record<string, unknown> | null;
      /** Drives the real removeBridge() path — lets a smoke exercise
       *  "Disconnect" (and its declined-peer side effect) deterministically. */
      remove(id: string): void;
      /** Drives the real addBridge() path. */
      add(input: Parameters<typeof addBridge>[0]): BridgeConfig;
      /** Live state of the offline path for a bridge. `served` is the one
       *  that matters: it counts requests the app's OWN traffic completed
       *  over WebTransport, so a smoke can tell "a session is configured"
       *  apart from "the app is actually using it" - the exact distinction
       *  a source-level gate cannot make. */
      wt(id: string): {
        present: boolean;
        url: string | null;
        open: boolean;
        served: number;
        usable: boolean;
        lastError: string | null;
      };
    };
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__opbuttonsBridges = {
    list: () => bridgesSig(),
    status: () => aggregateStatus(),
    paired: () => hasPairedBridge(),
    primaryId: () => primaryBridge()?.id ?? null,
    capabilities: (id) => { const cfg = resolveBridge(id); const h = cfg ? getConnection(cfg.id)?.health() : null; return (h?.capabilities as Record<string, unknown> | undefined) ?? null; },
    remove: (id) => removeBridge(id),
    add: (input) => addBridge(input),
    wt: (id) => {
      const conn = getWtSession(id);
      if (!conn) {
        return { present: false, url: null, open: false, served: 0, usable: false, lastError: null };
      }
      return {
        present: true,
        url: conn.url(),
        open: conn.isOpen(),
        served: conn.servedCount(),
        usable: conn.usable(),
        lastError: conn.lastError(),
      };
    },
  };
}
